/* OsCatalogImagesRenderer.js */

"use strict";
const _ = require("lodash");
const TreeComponent = require("@gluenetworks/tree_view").Tree;
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const MultipleSelectListControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Multiple.SelectList;

class OsCatalogImagesRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("os-catalog-images-gl");
        this._formFieldset = document.createElement("fieldset");
        this._imagesContainer = document.createElement("div");
        this._legend = document.createElement("legend");
        this._legend.innerHTML = "Images";
        this._treeComponent = undefined;
        this._selSelectBox = undefined;
        this._selControlContainer = document.createElement("sel-control-container");
        this._selectedImagesSection = new MultipleSelectListControl({
            options: [
                {
                    label: "",
                    disabled: true,
                    value: { id: "", name: "" }
                }
            ]
        });

        this._addButtonIcon = document.createElement("base-icon-gl");
        this._addButtonIcon.setAttribute("class", "btn-icon-chevron-right");
        this._addButtonIcon.dataset.icon = "common.chevron";

        this._removeButtonIcon = document.createElement("base-icon-gl");
        this._removeButtonIcon.setAttribute("class", "btn-icon-chevron-left");
        this._removeButtonIcon.dataset.icon = "common.chevron";

        this._removeAllButtonIcon = document.createElement("base-icon-gl");
        this._removeAllButtonIcon.setAttribute("class", "btn-icon-left");
        this._removeAllButtonIcon.dataset.icon = "common.back";

        this._addButton = new ButtonControl({});
        this._addButton._renderer._rootElem.appendChild(this._addButtonIcon);

        this._removeButton = new ButtonControl({});
        this._removeButton._renderer._rootElem.prepend(this._removeButtonIcon);

        this._removeAllButton = new ButtonControl({});
        this._removeAllButton._renderer._rootElem.prepend(this._removeAllButtonIcon);
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._treeComponent.on("UserSelectedPath", (this._onTreeUserOpenPath = this._onTreeUserOpenPath.bind(this)));
        this._treeComponent.on("UserSelectedPaths", (this._onTreeUserSelectedEditName = this._onTreeUserSelectedImages.bind(this)));
        this._treeComponent.on("UserSelectedPathForEditName", (this._onUserDoubleClick = this._onUserDoubleClick.bind(this)));
        this._addButton.on("UserClick", (this._onUserAddButtonClick = this._onUserAddButtonClick.bind(this)));
        this._removeButton.on("UserClick", (this._onUserRemoveButtonClick = this._onUserRemoveButtonClick.bind(this)));
        this._removeAllButton.on("UserClick", (this._onUserRemoveAllButtonClick = this._onUserRemoveAllButtonClick.bind(this)));

        this._selectedImagesSection.on("Value", (this._onValueSelected = this._onValueSelected.bind(this)));

        this._model._osCatalogDetailsView.on("RenderTree", (this._onRenderTree = this._onRenderTree.bind(this)));
    }

    _unregisterListeners() {
        this._treeComponent.removeListener("UserSelectedPath", this._onTreeUserOpenPath);
        this._treeComponent.removeListener("UserSelectedPaths", this._onTreeUserSelectedImages);
        this._treeComponent.removeListener("UserSelectedPathForEditName", this._onUserDoubleClick);
        this._addButton.removeListener("UserClick", this._onUserAddButtonClick);
        this._removeButton.removeListener("UserClick", this._onUserRemoveButtonClick);
        this._removeAllButton.removeListener("UserClick", this._onUserRemoveAllButtonClick);
        this._model._osCatalogDetailsView.removeListener("RenderTree", this._onRenderTree);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    render() {
        return this._model
            .whenModelIsReady()
            .then(() => {
                return Promise.all([this._renderImagesTreeView(), this._renderSelControls(), this._renderSelSelectbox()]);
            })
            .then(() => {
                this._imagesContainer.append(
                    this._treeComponent.renderer.rootElement,
                    this._selControlContainer,
                    this._selectedImagesSection.renderer.rootElement
                );
                this._formFieldset.appendChild(this._legend);
                this._formFieldset.appendChild(this._imagesContainer);
                this._rootElem.appendChild(this._formFieldset);
                if (this._model._gameEditor.userAccessToPlan) {
                    this._disableAllActions();
                } else {
                    this._registerListeners();
                }
            });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    shutdown() {
        return this._onShutdown();
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/

    _renderImagesTreeView() {
        return this._model.whenImagesModelIsReady().then((imagesFeed) => {
            let imageData = imagesFeed
                .filter((currentImage) => {
                    return !this._model._selectedImages.some((selectedImage) => {
                        return currentImage.id === selectedImage.id;
                    });
                })
                .map((imageEntity) => {
                    let image = this._getConvertedImageDataFromFeed(imageEntity, false);
                    return image;
                });

            const pathsImages = [].concat(_.sortBy(this._model._pathsData, "name"), _.sortBy(imageData, "name"));

            if (_.isUndefined(this._treeComponent)) {
                this._treeComponent = new TreeComponent({
                    autoOpen: false,
                    pageFeed: this._model.groupTree(pathsImages),
                    simple: true
                });
                this._disableAddAction();
                return Promise.resolve(this._treeComponent);
            } else {
                this._treeComponent.renderer.rootElement.innerHTML = "";
                this._treeComponent.updateTree(this._model.groupTree(pathsImages));
                return Promise.resolve(this._treeComponent);
            }
        });
    }

    _renderSelControls() {
        this._disableAddAction();
        return Promise.resolve().then(() => {
            this._selControlContainer.appendChild(this._addButton.renderer.rootElement);
            this._selControlContainer.appendChild(this._removeButton.renderer.rootElement);
            this._selControlContainer.appendChild(this._removeAllButton.renderer.rootElement);
            this._addButton.renderer.rootElement.classList.add("target-button");
            this._removeButton.renderer.rootElement.classList.add("target-button");
            this._removeAllButton.renderer.rootElement.classList.add("target-button");
        });
    }

    _renderSelSelectbox() {
        this.model._selectedImages = [];
        if (this.model._selectedImagesFeed.length > 0) {
            this.model._selectedImagesFeed.map((imageFeed) => {
                let selectedImage = this._getConvertedImageDataFromFeed(imageFeed, true);
                this.model._selectedImages.push(selectedImage);
            });

            let selectedImagesOptions = this._convertSelectListCompatibleImages();
            this._selectedImagesSection.setOptions(selectedImagesOptions);
        } else {
            this._disableRemoveAction();
            this._selectedImagesSection.setOptions([]);
        }
        return this._selectedImagesSection.renderer.render();
    }

    _updateSelSelectbox() {
        // this._model._reestablishfeed();
        this._model.whenSelectedImagesModelIsReady().then((feed) => {
            this.model._selectedImages = [];
            let data = feed;
            if (data.length > 0) {
                data.map((imageFeed) => {
                    let selectedImage = this._getConvertedImageDataFromFeed(imageFeed, true);
                    this.model._selectedImages.push(selectedImage);
                });
                let selectedImagesOptions = this._convertSelectListCompatibleImages();
                this._selectedImagesSection.setOptions(selectedImagesOptions);
            } else {
                this._disableRemoveAction();
            }
        });
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _onShutdown() {
        this._unregisterListeners();
        let shutdowns = [
            this._addButton.shutdown(),
            this._removeButton.shutdown(),
            this._removeAllButton.shutdown(),
            this._selectedImagesSection.shutdown(),
            this._treeComponent.shutdown()
        ];
        return Promise.all(shutdowns)
            .then(() => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._addButton = undefined;
                this._removeButton = undefined;
                this._removeAllButton = undefined;
                this._selectedImagesSection = undefined;
                this._selControlContainer = undefined;
                this._treeComponent = undefined;
                this._formFieldset = undefined;
                this._imagesContainer = undefined;
                this._legend = undefined;
                this._addButtonIcon = undefined;
                this._removeButtonIcon = undefined;
                this._removeAllButtonIcon = undefined;
            })
            .catch((err) => {
                console.error(err);
            });
    }

    _onTreeUserOpenPath(pathNode) {
        this._disableAddAction();
        if (pathNode.type === "image") {
            this._enableAddAction();
            this.model._currentSelection = [pathNode];
        } else {
            this.model._currentSelection = [];
        }
    }

    _onTreeUserSelectedImages(paths) {
        this._disableAddAction();
        if (paths.length >= 1) {
            this.model._currentSelection = _.filter(paths, ["type", "image"]);
            if (this.model._currentSelection.length > 0) {
                this._enableAddAction();
            }
        } else {
            this.model._currentSelection = [];
        }
    }

    _onUserDoubleClick(pathNode) {
        this._disableAddAction();
        if (pathNode.type === "image") {
            this._enableAddAction();
            this.model._currentSelection = [pathNode];
        }
        this._onUserAddButtonClick();
    }

    _onUserAddButtonClick() {
        this.model._currentSelection = _.uniqBy(this.model._currentSelection, "id");
        _.forEach(this.model._currentSelection, (currentImage) => {
            //removing pending relationships if image is already present inside Feed.
            const catalogPendinRelations = this.model._osCatalogEntity.getPendingRelationships();
            const foundInRelation = this._updateImageInRelation(catalogPendinRelations, currentImage, true);
            this.model._osCatalogEntity._pendingRelationships = catalogPendinRelations;
            let convertedSelectedImage = this._getConvertedImageDataFromSelectedImages(currentImage);
            this.model._selectedImages.push(convertedSelectedImage);
            if (!foundInRelation) {
                this._addRelatedImages(currentImage.id);
            }
        });

        if (this.model._osCatalogEntity.getPendingRelationships().length === 0) {
            this.model._gameEditor._tabCmpnt.setDirty(false);
            this.model._osCatalogDetailsView._renderer._disableViewActions();
        }
        this._updateSelectedImagesSection();
        this._enableRemoveAction(false);
        this._disableRemoveAction(false);
    }

    _onUserRemoveButtonClick() {
        let selectedValues = this._selectedImagesSection.getValue();
        // remove selectedValues from the options and re render select box.

        let remainingImages = this._selectedImagesSection.options.filter((option) => {
            return !selectedValues.some((val) => {
                return option.value.id === val.id;
            });
        });

        this._selectedImagesSection.options = remainingImages;
        this._selectedImagesSection.renderer.render();

        //check if the removed image has a pending relationship and remove it from pending relationships.
        _.forEach(selectedValues, (val) => {
            const catalogPendinRelations = this.model._osCatalogEntity.getPendingRelationships();
            const foundInRelation = this._updateImageInRelation(catalogPendinRelations, val);
            this.model._osCatalogEntity._pendingRelationships = catalogPendinRelations;
            if (!foundInRelation) {
                this._removeRelatedImages(val.id);
            }
        });

        // find the selected values from this.model._selectedImages. Remove them from here.
        let removedImagesData = [];
        let result = this.model._selectedImages.filter((image) => {
            return !selectedValues.some((val) => {
                if (image.id === val.id) {
                    let deselectedConvertedImages = this._getConvertedImageDataFromSelectedImages(image);
                    removedImagesData.push(deselectedConvertedImages);
                }
                return image.id === val.id;
            });
        });
        this._addImagesToPathNode(removedImagesData);
        this.model._selectedImages = result;
        if (this.model._osCatalogEntity.getPendingRelationships().length === 0) {
            this.model._gameEditor._tabCmpnt.setDirty(false);
            this.model._osCatalogDetailsView._renderer._disableViewActions();
        }
        if (this.model._selectedImages.length === 0) {
            this._disableRemoveAction();
        } else {
            this._disableRemoveAction(false);
        }
        if (this.model._currentSelection.length === 0) {
            this._disableAddAction();
        }
    }

    _onUserRemoveAllButtonClick() {
        let imagesData = this.model._selectedImages.map((image) => {
            let convertedImage = this._getConvertedImageDataFromSelectedImages(image);
            const catalogPendinRelations = this.model._osCatalogEntity.getPendingRelationships();
            const foundInRelation = this._updateImageInRelation(catalogPendinRelations, image);
            this.model._osCatalogEntity._pendingRelationships = catalogPendinRelations;
            if (!foundInRelation) {
                this._removeRelatedImages(image.id);
            }
            return convertedImage;
        });

        this._selectedImagesSection.setOptions([]);
        this._addImagesToPathNode(imagesData);
        this.model._selectedImages = [];
        this._disableAllActions();
        if (this.model._currentSelection.length !== 0) {
            this._enableAddAction();
        }

        if (this.model._osCatalogEntity.getPendingRelationships().length === 0) {
            this.model._gameEditor._tabCmpnt.setDirty(false);
            this.model._osCatalogDetailsView._renderer._disableViewActions();
        }
    }

    _updateImageInRelation(catalogPendinRelations, image, isAdd = false) {
        let foundInRelation = false;
        catalogPendinRelations.forEach((o, idx) => {
            if (o.rEntityId === image.id) {
                foundInRelation = true;
                if (!isAdd) {
                    catalogPendinRelations.splice(idx, 1);
                } else if (o.action === "remove") {
                    catalogPendinRelations.splice(idx, 1);
                }
            }
        });
        return foundInRelation;
    }

    _onValueSelected(val) {
        if (val.length === 0) {
            this._disableRemoveAction(false);
        } else {
            this._enableRemoveAction();
        }
    }

    _onRenderTree() {
        this._treeComponent.renderer.render();
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE ACTION METHODS */
    /*******************************************/

    _disableAddAction() {
        this._addButton.disabled = true;
    }

    _enableAddAction() {
        if (!this._model._gameEditor.userAccessToPlan) {
            this._addButton.disabled = false;
        }
    }

    _disableRemoveAction(disableAll = true) {
        this._removeButton.disabled = true;
        if (disableAll) {
            this._removeAllButton.disabled = true;
        }
    }

    _enableRemoveAction() {
        if (!this._model._gameEditor.userAccessToPlan) {
            this._removeButton.disabled = false;
            this._removeAllButton.disabled = false;
        }
    }

    _disableAllActions() {
        this._disableAddAction();
        this._disableRemoveAction();
    }

    /*******************************************/
    /* END PRIVATE ACTION METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _removeSelectedItemsFromTree(node) {
        Promise.resolve(this._treeComponent.removeNode(node));
    }

    _getConvertedImageDataFromFeed(currentImage, isSaved) {
        return {
            id: currentImage.id,
            parentId: currentImage.pristineProperties.pathId,
            name: this.getName(currentImage.pristineProperties.name),
            type: "image",
            fileType: currentImage.pristineProperties.fileType,
            fileSize: currentImage.pristineProperties.fileSize,
            imageType: currentImage.pristineProperties.imageType,
            isSaved: isSaved
        };
    }

    _getConvertedImageDataFromSelectedImages(currentImage) {
        return {
            id: currentImage.id,
            parentId: currentImage.parentId,
            name: currentImage.name,
            fileType: currentImage.fileType,
            fileSize: currentImage.fileSize,
            imageType: currentImage.imageType,
            type: "image",
            isSaved: currentImage.isSaved
        };
    }

    _updateSelectedImagesSection() {
        this.model._currentSelection.forEach((item) => {
            this._removeSelectedItemsFromTree(item);
        });
        let selectedImagesOptions = this._convertSelectListCompatibleImages();
        this._selectedImagesSection.setOptions(selectedImagesOptions);
    }

    _convertSelectListCompatibleImages() {
        let convertedImagesList = this.model._selectedImages.map((node) => {
            return {
                label: node.name,
                value: {
                    id: node.id,
                    name: node.name,
                    parentId: node.parentId,
                    type: "image",
                    fileType: node.fileType,
                    fileSize: node.fileSize,
                    imageType: node.imageType
                }
            };
        });
        return convertedImagesList;
    }

    _addImagesToPathNode(imageData) {
        Promise.resolve(this._treeComponent.updateNode(imageData));
    }

    _addRelatedImages(imageId) {
        this.model.osCatalogEntity.addToOsImagesRelationship(imageId);
    }

    _removeRelatedImages(imageId) {
        this.model.osCatalogEntity.removeFromOsImagesRelationship(imageId);
    }

    getName(name) {
        // Removing UUID prefix
        if (name[36] === "/") {
            name = name.substring(37);
        }
        return name;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = OsCatalogImagesRenderer;
